<template>
  <section class="module-panel panel" :aria-labelledby="questionHeadingId">
    <div class="panel-body">
      <div class="heading-container">
        <div class="heading-title">
          <div
            :id="questionHeadingId"
            class="question-container question-heading"
            role="heading"
            aria-level="2"
          >
            <span class="question-number">{{ question.number }}. </span>
            <span :class="answerClass" class="question" v-html="getMarkdown(question.question)"></span>
          </div>
          <p
            v-if="question.select_all_that_apply"
            :id="selectInstructionId"
            class="question-instruction"
          >
            {{ expectedAnswerTotals }}
          </p>
        </div>
        <div v-if="showResult" class="question-result" role="status">
          <span v-if="correct" class="correct">
            <span class="sr-only">{{ resultStatusText }}</span>
            <i class="fas fa-check" aria-hidden="true"></i>
          </span>
          <span v-else class="wrong">
            <span class="sr-only">{{ resultStatusText }}</span>
            <i class="fas fa-times" aria-hidden="true"></i>
          </span>
        </div>
        <details v-else-if="question.hint" class="question-hint">
          <summary class="question-hint-summary">
            Hint<span class="sr-only">. Press to show or hide.</span>
            <i class="fas fa-question-circle" aria-hidden="true"></i>
          </summary>
          <div class="question-hint-body k-text-primary" v-html="question.hint"></div>
        </details>
      </div>
      <div v-if="question.resources && question.resources.length > 0" class="row question-resources">
        <k-gallery v-if="imageResources.length > 0" :images="imageResources"></k-gallery>
      </div>
      <fieldset
        class="responses-fieldset"
        :aria-labelledby="questionHeadingId"
        :aria-describedby="fieldAriaDescribedBy"
      >
        <ul class="responses-list" :class="{ 'read-only': readOnly || showResult }">
          <template v-if="showResult && previousAnswer">
            <li v-for="ans in question.answers" :key="ans.id">
              <input
                v-if="getInputType(question) === 'radio'"
                class="answer"
                readonly
                :id="inputId(ans)"
                type="radio"
                :name="question.id"
                :value="ans.id"
                :checked="previousAnswer[0] === ans.id"
                :tabindex="nonInteractiveTabindex"
              >
              <input
                v-else
                class="answer"
                :id="inputId(ans)"
                :type="getInputType(question)"
                :name="question.id"
                :value="ans.id"
                :checked="previousAnswer.indexOf(ans.id) > -1"
                :tabindex="nonInteractiveTabindex"
              >
              <label :for="inputId(ans)">{{ ans.answer }}</label>
              <div class="check" :class="getInputType(question)" aria-hidden="true"><div class="inside"></div></div>
            </li>
          </template>
          <template v-else>
            <li v-for="ans in question.answers" :key="ans.id">
              <input
                v-if="getInputType(question) === 'radio'"
                v-model="currentAnswer[0]"
                class="answer"
                :id="inputId(ans)"
                type="radio"
                :name="question.id"
                :value="ans.id"
                :tabindex="nonInteractiveTabindex"
              >
              <input
                v-else
                v-model="currentAnswer"
                class="answer"
                :id="inputId(ans)"
                :type="getInputType(question)"
                :name="question.id"
                :value="ans.id"
                :tabindex="nonInteractiveTabindex"
              >
              <label :for="inputId(ans)">{{ ans.answer }}</label>
              <div class="check" :class="getInputType(question)" aria-hidden="true"><div class="inside"></div></div>
            </li>
          </template>
        </ul>
      </fieldset>
    </div>
  </section>
</template>

<style>
.question-container span p {
  color: var(--kate-type-light);
}
</style>

<style scoped>
.heading-container {
  margin: 0;
  flex-wrap: nowrap;
}

.responses-fieldset {
  border: 0;
  margin: 0;
  padding: 0;
  min-width: 0;
}

.question-container {
  display: flex;
  flex-wrap: nowrap;
  font-size: 1.35em;
}

.question-number {
  padding-right: 5px;
  min-width: 30px;
}

.question {
  width: 95%;
}

.question-container span p,
.question-instruction {
  color: var(--kate-type-light);
}

.question-result {
  padding: 20px 10px 10px;
}

.question-result span {
  font-size: 1.25em;
}

.heading-title .question-heading.correct {
  border-bottom: 2px solid var(--kate-success);
  color: var(--kate-success-alt);
}

.question-result span.correct {
  color: var(--kate-success);
}

.heading-title .question-heading.wrong {
  border-bottom: 2px solid var(--kate-danger);
  color: var(--kate-danger-light);
}

.question-result span.wrong {
  color: var(--kate-danger);
}

.question-resources {
  margin-bottom: 15px;
  padding: 0;
}

.responses-list {
  list-style: none;
  padding-left: 0;
}

.responses-list li {
  position: relative;
  color: var(--kate-type-primary);
  width: fit-content;
}

/* Visually hide native controls while keeping them keyboard- and screen-reader-accessible
   (global custom.css uses visibility:hidden on ul li inputs, which removes them from AT). */
.responses-list li input.answer {
  position: absolute;
  left: 0;
  top: 50%;
  width: 22px;
  height: 22px;
  margin: 0;
  z-index: 10;
  opacity: 0;
  cursor: pointer;
  visibility: visible;
}

.responses-list li input.answer:focus-visible + label {
  outline: 2px solid var(--kate-type-link);
  outline-offset: 2px;
  border-radius: 2px;
}

.responses-list li label {
  padding-left: 35px;
}

.read-only {
  pointer-events: none;
}

.question-hint {
  display: inline-block;
  margin: 15px 0;
  min-width: 50px;
}

.question-hint-summary {
  cursor: pointer;
  list-style: none;
  color: var(--kate-type-blue);
  padding: 2px 0;
}

.question-hint-summary::-webkit-details-marker {
  display: none;
}

.question-hint-summary i {
  transition: color 0.3s;
  padding: 5px 0 5px 4px;
}

.question-hint[open] .question-hint-summary i,
.question-hint-summary:hover i {
  color: var(--kate-secondary);
}

.question-hint-body {
  margin-top: 8px;
  padding: 8px;
  border-radius: 2px;
  border: var(--border-secondary);
  max-width: 28rem;
}

@media screen and (max-width: 479px) {
  .category-text {
    position: relative;
  }

  .question-hint {
    position: unset;
  }
}
</style>

<script>
import KGallery from '@base-components/k-gallery.vue';
import { markdown } from '@utils/maths-markdown';

export default {
  components: {
    KGallery,
  },

  props: {
    question: {
      type: Object,
    },
    previousAnswer: {
      type: Array,
      default: () => [],
    },
    correct: {
      type: Boolean,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    showResult: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      currentAnswer: [],
    };
  },

  watch: {
    currentAnswer() {
      this.emitAnswer();
    },
  },

  created() {
    this.currentAnswer = this.previousAnswer;
  },

  computed: {
    questionHeadingId() {
      return `quiz-question-${this.question.id}-heading`;
    },
    selectInstructionId() {
      return `quiz-question-${this.question.id}-instruction`;
    },
    fieldAriaDescribedBy() {
      if (this.question.select_all_that_apply) {
        return this.selectInstructionId;
      }
      return undefined;
    },
    resultStatusText() {
      if (this.correct) {
        return 'Correct';
      }
      return 'Incorrect';
    },
    nonInteractiveTabindex() {
      return this.readOnly || this.showResult ? -1 : undefined;
    },
    answerClass() {
      if (!this.showResult) {
        return '';
      }
      if (this.correct) {
        return 'correct';
      }
      return 'wrong';
    },
    expectedAnswerTotals() {
      if (this.question?.n_correct_answers > 1) {
        return `Select ${this.question.n_correct_answers} that apply.`;
      }
      return 'Select all that apply.';
    },
    imageResources() {
      return this.question.resources.filter(x => x.resource_type === 'image').map(x => x.resource_link);
    },
  },

  methods: {
    inputId(ans) {
      return `quiz-question-${this.question.id}-answer-${ans.id}`;
    },

    getInputType(question) {
      if (question.select_all_that_apply) {
        return 'checkbox';
      }
      return 'radio';
    },

    emitAnswer() {
      this.$emit('update', this.currentAnswer);
    },

    clearAnswer() {
      this.currentAnswer = [];
    },

    getMarkdown(str) {
      return markdown(str, { openLinksInNewTab: true });
    },

  },
};
</script>
